import { SearchInput } from "@/components/shared/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type LogTimeRange = "1h" | "24h" | "7d" | "all";

export interface LogFilterValues {
  level: string;
  category: string;
  status: string;
  method: string;
  path: string;
  range: LogTimeRange;
}

interface LogFiltersProps {
  values: LogFilterValues;
  onChange: (patch: Partial<LogFilterValues>) => void;
}

const LEVEL_OPTIONS = [
  { value: "all", label: "All levels" },
  { value: "error", label: "Error" },
  { value: "warn", label: "Warn" },
  { value: "info", label: "Info" },
  { value: "debug", label: "Debug" },
];

const CATEGORY_OPTIONS = [
  { value: "all", label: "All categories" },
  { value: "request", label: "Request" },
  { value: "error", label: "Error" },
  { value: "app", label: "App" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "2xx", label: "2xx" },
  { value: "3xx", label: "3xx" },
  { value: "4xx", label: "4xx" },
  { value: "5xx", label: "5xx" },
];

const METHOD_OPTIONS = [
  { value: "all", label: "All methods" },
  { value: "GET", label: "GET" },
  { value: "POST", label: "POST" },
  { value: "PATCH", label: "PATCH" },
  { value: "PUT", label: "PUT" },
  { value: "DELETE", label: "DELETE" },
];

const RANGE_OPTIONS: Array<{ value: LogTimeRange; label: string }> = [
  { value: "1h", label: "Last hour" },
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "all", label: "All" },
];

function isLogTimeRange(value: string): value is LogTimeRange {
  return value === "1h" || value === "24h" || value === "7d" || value === "all";
}

function FilterSelect({
  value,
  onValueChange,
  options,
  className,
}: {
  value: string;
  onValueChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  className?: string;
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className={className}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function LogFilters({ values, onChange }: LogFiltersProps) {
  return (
    <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
      <SearchInput
        value={values.path}
        onValueChange={(path) => onChange({ path })}
        placeholder="Search path..."
        className="md:w-64"
      />
      <FilterSelect
        value={values.level}
        onValueChange={(level) => onChange({ level })}
        options={LEVEL_OPTIONS}
        className="w-full md:w-[140px]"
      />
      <FilterSelect
        value={values.category}
        onValueChange={(category) => onChange({ category })}
        options={CATEGORY_OPTIONS}
        className="w-full md:w-[150px]"
      />
      <FilterSelect
        value={values.status}
        onValueChange={(status) => onChange({ status })}
        options={STATUS_OPTIONS}
        className="w-full md:w-[140px]"
      />
      <FilterSelect
        value={values.method}
        onValueChange={(method) => onChange({ method })}
        options={METHOD_OPTIONS}
        className="w-full md:w-[140px]"
      />
      <FilterSelect
        value={values.range}
        onValueChange={(range) => {
          if (isLogTimeRange(range)) onChange({ range });
        }}
        options={RANGE_OPTIONS}
        className="w-full md:w-[150px]"
      />
    </div>
  );
}
